import type { ReadableStream } from 'node:stream/web';
import https from 'https';
import http from 'http';
import fs from 'fs';
import fetch from 'cross-fetch';
import { getProxySettings } from 'get-proxy-settings';
import { ProxyAgent } from 'proxy-agent';
import { isEqual } from 'lodash';
import { Injectable } from '@gitlab-org/di';
import { LsFetch, FetchBase, FetchAgentOptions, FetchHeaders } from '../common/fetch';
import { log } from '../common/log';
import { handleFetchError } from '../common/handle_fetch_error';
import { REQUEST_TIMEOUT_MILLISECONDS } from '../common/constants';
import { TimeoutError } from '../common/fetch_error';

const hasName = (n: unknown): n is { name: unknown } => Boolean((n as { name?: unknown }).name);

const httpAgent = new http.Agent({
  keepAlive: true,
});

export interface LsRequestInit extends RequestInit {
  agent: ProxyAgent | https.Agent | http.Agent;
}

interface LsAgentOptions {
  rejectUnauthorized: boolean;
  ca?: Buffer;
  cert?: Buffer;
  key?: Buffer;
}

/**
 * Wrap fetch to support proxy configurations
 */
@Injectable(LsFetch, [])
export class Fetch extends FetchBase implements LsFetch {
  #proxy?: ProxyAgent;

  #userProxy?: string;

  #initialized: boolean = false;

  #httpsAgent: https.Agent;

  #agentOptions: Readonly<LsAgentOptions>;

  constructor(userProxy?: string) {
    super();

    this.#agentOptions = {
      rejectUnauthorized: true,
    };
    this.#userProxy = userProxy;
    this.#httpsAgent = this.#createHttpsAgent();
  }

  async initialize(): Promise<void> {
    // Set http_proxy and https_proxy environment variables
    // which will then get picked up by the ProxyAgent and
    // used.
    try {
      const proxy = await getProxySettings();
      if (proxy?.http) {
        process.env.http_proxy = `${proxy.http.protocol}://${proxy.http.host}:${proxy.http.port}`;
        log.debug(`fetch: Detected http proxy through settings: ${process.env.http_proxy}`);
      }

      if (proxy?.https) {
        process.env.https_proxy = `${proxy.https.protocol}://${proxy.https.host}:${proxy.https.port}`;
        log.debug(`fetch: Detected https proxy through settings: ${process.env.https_proxy}`);
      }

      if (!proxy?.http && !proxy?.https) {
        log.debug(`fetch: Detected no proxy settings`);
      }
    } catch (err) {
      log.warn('Unable to load proxy settings', err);
    }

    if (this.#userProxy) {
      log.debug(`fetch: Detected user proxy ${this.#userProxy}`);
      process.env.http_proxy = this.#userProxy;
      process.env.https_proxy = this.#userProxy;
    }

    if (process.env.http_proxy || process.env.https_proxy) {
      log.info(
        `fetch: Setting proxy to https_proxy=${process.env.https_proxy}; http_proxy=${process.env.http_proxy}`,
      );
      this.#proxy = this.#createProxyAgent();
    }

    this.#initialized = true;
  }

  async destroy(): Promise<void> {
    this.#proxy?.destroy();
  }

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!this.#initialized) {
      throw new Error('LsFetch not initialized. Make sure LsFetch.initialize() was called.');
    }

    try {
      return await this.#fetchLogged(input, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MILLISECONDS),
        ...init,
        agent: this.#getAgent(input),
      });
    } catch (e) {
      if (hasName(e) && e.name === 'AbortError') {
        throw new TimeoutError(input);
      }
      throw e;
    }
  }

  updateAgentOptions({ ignoreCertificateErrors, ca, cert, certKey }: FetchAgentOptions): void {
    let fileOptions = {};
    try {
      fileOptions = {
        ...(ca ? { ca: fs.readFileSync(ca) } : {}),
        ...(cert ? { cert: fs.readFileSync(cert) } : {}),
        ...(certKey ? { key: fs.readFileSync(certKey) } : {}),
      };
    } catch (err) {
      log.error('Error reading https agent options from file', err);
    }

    const newOptions: LsAgentOptions = {
      rejectUnauthorized: !ignoreCertificateErrors,
      ...fileOptions,
    };

    if (isEqual(newOptions, this.#agentOptions)) {
      // new and old options are the same, nothing to do
      return;
    }

    this.#agentOptions = newOptions;
    this.#httpsAgent = this.#createHttpsAgent();
    if (this.#proxy) {
      this.#proxy = this.#createProxyAgent();
    }
  }

  async *streamFetch(
    url: string,
    body: string,
    headers: FetchHeaders,
  ): AsyncGenerator<string, void, void> {
    let buffer = '';
    const decoder = new TextDecoder();

    async function* readStream(
      stream: ReadableStream<Uint8Array>,
    ): AsyncGenerator<string, void, void> {
      for await (const chunk of stream) {
        buffer += decoder.decode(chunk);
        yield buffer;
      }
    }

    const response: Response = await this.fetch(url, {
      method: 'POST',
      headers,
      body,
    });

    await handleFetchError(response, 'Code Suggestions Streaming');

    if (response.body) {
      // Note: Using (node:stream).ReadableStream as it supports async iterators
      yield* readStream(response.body as ReadableStream);
    }
  }

  #createHttpsAgent(): https.Agent {
    const agentOptions = {
      ...this.#agentOptions,
      keepAlive: true,
    };
    log.debug(
      `fetch: https agent with options initialized ${JSON.stringify(
        this.#sanitizeAgentOptions(agentOptions),
      )}.`,
    );
    return new https.Agent(agentOptions);
  }

  #createProxyAgent(): ProxyAgent {
    log.debug(
      `fetch: proxy agent with options initialized ${JSON.stringify(
        this.#sanitizeAgentOptions(this.#agentOptions),
      )}.`,
    );
    return new ProxyAgent(this.#agentOptions);
  }

  async #fetchLogged(input: RequestInfo | URL, init: LsRequestInit): Promise<Response> {
    const start = Date.now();
    const url = this.#extractURL(input);

    if (init.agent === httpAgent) {
      log.debug(`fetch: request for ${url} made with http agent.`);
    } else {
      const type = init.agent === this.#proxy ? 'proxy' : 'https';
      log.debug(`fetch: request for ${url} made with ${type} agent.`);
    }

    try {
      const resp = await fetch(input, init);
      const duration = Date.now() - start;

      log.debug(`fetch: request to ${url} returned HTTP ${resp.status} after ${duration} ms`);

      return resp;
    } catch (e) {
      const duration = Date.now() - start;
      log.debug(`fetch: request to ${url} threw an exception after ${duration} ms`);
      log.error(`fetch: request to ${url} failed with:`, e);
      throw e;
    }
  }

  #getAgent(input: RequestInfo | URL): ProxyAgent | https.Agent | http.Agent {
    if (this.#proxy) {
      return this.#proxy;
    }
    if (input.toString().startsWith('https://')) {
      return this.#httpsAgent;
    }

    return httpAgent;
  }

  #extractURL(input: RequestInfo | URL): string {
    if (input instanceof URL) {
      return input.toString();
    }

    if (typeof input === 'string') {
      return input;
    }

    return input.url;
  }

  #sanitizeAgentOptions(agentOptions: LsAgentOptions) {
    const { ca, cert, key, ...options } = agentOptions;
    return {
      ...options,
      ...(ca ? { ca: '<hidden>' } : {}),
      ...(cert ? { cert: '<hidden>' } : {}),
      ...(key ? { key: '<hidden>' } : {}),
    };
  }
}
